import {
  Component,
  Input,
  OnDestroy,
  OnInit,
} from "@angular/core";

import { MatSelectionListChange } from "@angular/material/list";

import { TranslateService } from "@ngx-translate/core";

import { Observable, Subscription, zip } from "rxjs";
import { skip } from "rxjs/operators";
import { select, Store } from "@ngrx/store";
import {
  createStoreProductList,
  getSiteStoreProductList,
} from "src/app/store/actions/sites/sites.actions";
import { getProductsList } from "src/app/store/actions/products/product.actions";
import {
  AppState,
  loadedProductListState,
  siteStoreProductListLoadedState,
} from "src/app/store/reducers";

import { SiteProductDTO } from "src/app/models/Sites/siteProductDTO";
import { ProductDTO } from "src/app/models/Products/productDTO";
import { FieldType, FilterName } from "src/app/models/custom-table/custom-table.model";
import { SelectionItem } from "src/app/models/selection-list/selection-list.model";
import { SnackbarService } from "src/app/shared/services/snackbar/snackbar.service";
import { SnackbarType } from "src/app/models/snackbar/snackbar.model";

@Component({
  selector: "store-products",
  templateUrl: "./store-products.component.html",
  styleUrls: ["./store-products.component.scss"],
})
export class StoreProductsComponent implements OnInit, OnDestroy {
  @Input() siteCode: string;

  multiSubs: Subscription;

  //Store Products
  storeProductsList$: Observable<ProductDTO[]>;
  storeProductsList: ProductDTO[];
  storeProductsListSub: Subscription;

  //Selected Store Products
  selectedStoreProducts$: Observable<SiteProductDTO[]>;
  selectedStoreProducts: SiteProductDTO[];

  searchTerms: string[] = [];

  isNotFuelFilter = {
    column: "isFuel",
    fieldType: FieldType.BOOLEAN,
    operator: FilterName.EQUAL,
    value: false
  };

  constructor(
    private translate: TranslateService,
    private store: Store<AppState>,
    private snackbarService: SnackbarService
  ) {
    this.storeProductsList$ = this.store.pipe(select(loadedProductListState));
    this.selectedStoreProducts$ = this.store.pipe(select(siteStoreProductListLoadedState));
  }

  ngOnInit() {
    this.setSubscriptions();
    this.setDispatchers();
  }
  
  setSubscriptions() {
    this.multiSubs = zip(
      this.storeProductsList$.pipe(skip(1)),
      this.selectedStoreProducts$.pipe(skip(1))
    ).subscribe(([storeProductsList, selectedStoreProducts]) => {
      if (storeProductsList) {
        this.storeProductsList = this.setStoreProductsList(storeProductsList);
      }

      if (selectedStoreProducts) {
        this.selectedStoreProducts = this.setSelectedStoreProducts(selectedStoreProducts);
      }

      if (storeProductsList && selectedStoreProducts) {
        this.storeProductsList = this.setStoreProductsListWithGrossUnitPrice(storeProductsList);
      }
    });
    this.storeProductsListSub = this.storeProductsList$
      .pipe(skip(1))
      .subscribe((storeProductsList) => {
        if (storeProductsList) {
          this.storeProductsList = this.setStoreProductsList(storeProductsList);
        }
        if (storeProductsList && this.selectedStoreProducts) {
          this.storeProductsList = this.setStoreProductsListWithGrossUnitPrice(storeProductsList);
        }
      });
  }

  setStoreProductsList(storeProductsList: ProductDTO[]) {
    return storeProductsList.map((product) => ({
      ...product,
      style: product.color
        ? `background-color: #${product.color}; color: #FFF;`
        : null,
    }));
  }

  setSelectedStoreProducts(selectedStoreProducts: SiteProductDTO[]) {
    return selectedStoreProducts.map(
      (selectedProduct) => ({ 
        productCode: selectedProduct.productCode, 
        favorite: selectedProduct.favorite,
        enabled: selectedProduct.enabled,
        grossUnitPrice:
          selectedProduct.grossUnitPrice ||
          selectedProduct.grossUnitPrice === 0
            ? selectedProduct.grossUnitPrice
            : selectedProduct.referenceUnitPrice ||
              selectedProduct.referenceUnitPrice === 0
            ? selectedProduct.referenceUnitPrice
            : 0,
      })
    );
  }

  setStoreProductsListWithGrossUnitPrice(storeProductsList: ProductDTO[]) {
    return storeProductsList.map((product) => {
      const { code } = product;
      const productFound = this.selectedStoreProducts.find(({ productCode }) => productCode === code);
      return { ...product, grossUnitPrice: productFound?.grossUnitPrice };
    }); 
  }
  
  setDispatchers() {
    this.store.dispatch(
      getProductsList({
        options: {
          advancedFilteringArgs: { advancedFilter: [ this.isNotFuelFilter ]},
        },
      })
    );
    this.store.dispatch(
      getSiteStoreProductList({ code: this.siteCode })
    );
  }

  checkSelected(item: SelectionItem): boolean {
    const { code } = item;
    if (!this.selectedStoreProducts || !code) {
      return;
    }
    return this.selectedStoreProducts.some(
      ({ productCode }) => productCode === code
    );
  }

  onSelectionChange(event: any, multipleSelection = false) {
    const [option] = event.options;
    const { value: { code } } = option;

    const found = !multipleSelection && this.selectedStoreProducts
      ? this.selectedStoreProducts.some(
          ({ productCode }) => productCode === code
        )
      : false;
    if (found) {
      this.selectedStoreProducts = this.selectedStoreProducts.filter(
        ({ productCode }) => productCode !== code
      );
    } else {
      this.selectedStoreProducts = this.selectedStoreProducts
        ? [
            ...this.selectedStoreProducts,
            {
              productCode: code,
              favorite: null,
              enabled: true,
              grossUnitPrice: undefined,
            },
          ]
        : [
            {
              productCode: code,
              favorite: null,
              enabled: true,
              grossUnitPrice: undefined,
            },
          ];
    }

    if (!multipleSelection) {
      this.updateSiteStoreProducts(this.selectedStoreProducts);
    }
  }

  setStoreProductSearchTooltip() {
    const searchTooltipHint = this.translate.instant("SimpleFilter.HintTitle");
    const searchableFields = [
      this.translate.instant("SiteManagement.Code"),
      this.translate.instant("SiteManagement.Description"),
    ];
    return `${searchTooltipHint}\n${searchableFields.join("\n")}`;
  }

  searchStoreProducts(searchTerms: string[]) {
    this.searchTerms = searchTerms;
    this.store.dispatch(
      getProductsList({
        options: {
          filteringArgs: {
            filterConfig: [{ field: "code" }, { field: "description" }],
            searchTerms,
          },
          advancedFilteringArgs: { advancedFilter: [ this.isNotFuelFilter ]},
        },
      })
    );
  }

  checkProductSelected(product: ProductDTO) {
    const { code } = product;
    if (!this.selectedStoreProducts) {
      return true;
    }
    return !this.selectedStoreProducts.some(
      ({ productCode }) => productCode === code
    );
  }


  checkIsItemFavorite(product: ProductDTO) {
    const { code } = product;
    return this.selectedStoreProducts.some(({ productCode, favorite }) => productCode === code && favorite);
  } 

  favoriteClicked(product: ProductDTO) {
    const { code } = product;

    this.selectedStoreProducts = this.selectedStoreProducts.reduce((acc, cur) => {
      if (cur.productCode === code) {
        return [...acc, { ...cur, favorite: !cur.favorite }]
      }
      return [ ...acc, cur ];
    }, []);
    
    this.updateSiteStoreProducts(this.selectedStoreProducts);
  }

  checkIsItemEnabled(product: ProductDTO) {
    const { code } = product;
    return this.selectedStoreProducts.some(({ productCode, enabled }) => productCode === code && enabled);
  }

  enableClicked(product: ProductDTO) {
    const { code } = product;

    this.selectedStoreProducts = this.selectedStoreProducts.reduce((acc, cur) => {
      if (cur.productCode === code) {
        return [...acc, { ...cur, enabled: !cur.enabled }]
      }
      return [ ...acc, cur ];
    }, []);

    this.updateSiteStoreProducts(this.selectedStoreProducts);
  }

  priceChanged(product: ProductDTO) {
    const { code, grossUnitPrice } = product;

    this.selectedStoreProducts = this.selectedStoreProducts.reduce((acc, cur) => {
      if (cur.productCode === code) {
        return [...acc, { ...cur, grossUnitPrice }]
      }
      return [ ...acc, cur ];
    }, []);

    product.error = grossUnitPrice < 0 || grossUnitPrice > 999999.9999;
    
    if (this.storeProductsList.some((product) => product.error)) {
      this.snackbarService.show({
        message: this.translate.instant("Products.storeProductPriceInvalidExtended"),
        type: SnackbarType.ERROR,
      });
      return;
    }

    this.updateSiteStoreProducts(this.selectedStoreProducts);
  }

  selectAll() {
    this.storeProductsList.forEach(({ code }) => {
      this.onSelectionChange(
        {
          options: [{ value: { code } }],
        },
        true
      );
    });
    this.updateSiteStoreProducts(this.selectedStoreProducts);
  }

  unselectAll() {
    this.selectedStoreProducts = [];
    this.updateSiteStoreProducts(this.selectedStoreProducts);
  }

  updateSiteStoreProducts(selectedStoreProducts: SiteProductDTO[]) {
    const siteStoreProductsList = selectedStoreProducts.map(
      ({ productCode, favorite, enabled, grossUnitPrice }) => ({ 
        siteCode: this.siteCode, 
        productCode,
        favorite,
        enabled,
        grossUnitPrice,
      })
    );

    this.store.dispatch(
      createStoreProductList({
        siteCode: this.siteCode,
        productList: siteStoreProductsList,
        searchTerms: this.searchTerms,
      })
    );
  }

  ngOnDestroy() {
    if (this.multiSubs) {
      this.multiSubs.unsubscribe();
    }
    if (this.storeProductsListSub) {
      this.storeProductsListSub.unsubscribe();
    }
  }
}
